import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import {
  editSaved,
  clearPuzzle,
  saveConfiguration,
  updateConfiguration,
  deleteConfiguration,
  loadConfiguraciones,
} from "../../store/reducers/phraseBuilder/phraseBuilder.slice";
import { Icon } from "@iconify/react/dist/iconify.js";
import EmptyState from "../UI/EmptyState";

const SavedPhrases = () => {
  const dispatch = useDispatch();
  const { saved, editingId, puzzle, loading } = useSelector((state) => state.phraseBuilder);

  // Cargar configuraciones al montar el componente
  useEffect(() => {
    dispatch(loadConfiguraciones());
  }, [dispatch]);

  const handleSave = async () => {
    if (!puzzle.left || !puzzle.right) return;

    try {
      if (editingId !== null) {
        // Actualizar configuración existente
        await dispatch(
          updateConfiguration({
            id: editingId,
            frase1Id: puzzle.left.id,
            frase2Id: puzzle.right.id,
          })
        ).unwrap();
      } else {
        // Crear nueva configuración
        await dispatch(
          saveConfiguration({
            frase1Id: puzzle.left.id,
            frase2Id: puzzle.right.id,
          })
        ).unwrap();
      }
      
      // Recargar la lista completa después de guardar o actualizar
      dispatch(loadConfiguraciones());
    } catch (error) {
      console.error("Error al guardar:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteConfiguration(id)).unwrap();
      // Recargar la lista después de eliminar
      dispatch(loadConfiguraciones());
    } catch (error) {
      console.error("Error al eliminar:", error);
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-semibold mb-0 d-flex align-items-center gap-2">Frases configuradas</h6>
          <button
            className="btn btn-success btn-sm d-flex align-items-center gap-2"
            onClick={handleSave}
            disabled={loading || !puzzle.left || !puzzle.right}
          >
            <Icon icon="mdi:check-circle-outline" width={16} />
            {editingId !== null ? "Actualizar" : "Guardar"}
          </button>
        </div>

        {saved.length === 0 && !loading && (
          <EmptyState
            icon="mdi:archive-outline"
            title="No hay frases guardadas aún"
          />
        )}

        {loading && (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        )}

        {saved.length > 0 && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }} className="border rounded">
            {saved.map((s, i) => (
              <div key={s.id} className={`p-3 ${i > 0 ? 'border-top' : ''}`}>
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="mb-2">
                      <div className="d-flex align-items-start gap-2">
                        <Icon icon="mdi:arrow-left-bold" width={16} className="text-primary flex-shrink-0 mt-1" />
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                          <div className="text-muted small mb-1">
                            Izquierda
                            {s.frase_1_detalle?.talento_nombre && (
                              <span className="ms-2 badge bg-primary-subtle text-primary">
                                {s.frase_1_detalle.talento_nombre}
                              </span>
                            )}
                            {s.frase_1_detalle?.is_random && (
                              <span className="ms-2 badge bg-info-subtle text-info">
                                <Icon icon="mdi:shuffle-variant" width={12} className="me-1" />
                                Random
                              </span>
                            )}
                          </div>
                          <div>{s.frase_1_detalle?.frase || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="d-flex align-items-start gap-2">
                        <Icon icon="mdi:arrow-right-bold" width={16} className="text-success flex-shrink-0 mt-1" />
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                          <div className="text-muted small mb-1">
                            Derecha
                            {s.frase_2_detalle?.talento_nombre && (
                              <span className="ms-2 badge bg-success-subtle text-success">
                                {s.frase_2_detalle.talento_nombre}
                              </span>
                            )}
                            {s.frase_2_detalle?.is_random && (
                              <span className="ms-2 badge bg-info-subtle text-info">
                                <Icon icon="mdi:shuffle-variant" width={12} className="me-1" />
                                Random
                              </span>
                            )}
                          </div>
                          <div>{s.frase_2_detalle?.frase || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <button
                      className="btn btn-sm btn-outline-warning"
                      style={{ width: 32, height: 32, padding: 0 }}
                      onClick={() => dispatch(editSaved(i))}
                      title="Editar"
                      disabled={loading}
                    >
                      <Icon icon="mdi:pencil-outline" width={14} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      style={{ width: 32, height: 32, padding: 0 }}
                      onClick={() => handleDelete(s.id)}
                      title="Eliminar"
                      disabled={loading}
                    >
                      <Icon icon="mdi:delete-outline" width={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedPhrases;
